'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { ScheduleContextAuthority, ScheduleWriteIdentity, ScheduleWriteOperation } from './useScheduleWriteAuthority';

export interface SavedScheduleSnapshot { name: string; classes: readonly string[]; configuration: unknown }
export interface ScheduleSaveLabel { scheduleName: string; termLabel: string; candidate: number | null; makePrimary: boolean }
export interface ScheduleSaveOutcome extends ScheduleSaveLabel { state: 'pending' | 'error' | 'success'; savedAt?: string }
interface Options { ownerId: string | null; scheduleId: string | null; draft: unknown; snapshot: SavedScheduleSnapshot; isSelected: boolean; isDirty: boolean }
interface Receipt { originalDraft: unknown; label: ScheduleSaveLabel; context: ScheduleContextAuthority; originalCurrent: () => boolean;
  state: ScheduleSaveOutcome['state']; snapshotKey?: string; savedAt?: string }
function snapshotKey(value: unknown): string {
  const sorted = (item: unknown): unknown => Array.isArray(item) ? item.map(sorted)
    : item !== null && typeof item === 'object' ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)).map(([key, entry]) => [key, sorted(entry)])) : item;
  return JSON.stringify(sorted(value));
}
/** Only the already acknowledged async direct-write publisher uses this boundary. */
export function commitScheduleSavePublication(publish: () => void): void { flushSync(publish); }

/** Only this exact still-held context may exempt its own Save from the shared term gate. */
export function isGenerationBlockedByScheduleWrite(sharedPending: boolean, ownOperation: ScheduleWriteOperation | null,
  current: Omit<ScheduleWriteIdentity, 'ownerId' | 'scheduleId'> & { ownerId: string | null; scheduleId: string | null }): boolean {
  if (!sharedPending) return false;
  const identity = ownOperation?.identity;
  return !identity || identity.ownerId !== current.ownerId || identity.scheduleId !== current.scheduleId
    || identity.routeKey !== current.routeKey || identity.term !== current.term || ownOperation?.isContextCurrent() !== true;
}

/** This projection owns no admission, transport, retries or shared pending lock. */
export function useScheduleSaveOutcome({ ownerId, scheduleId, draft, snapshot, isSelected, isDirty }: Options) {
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const acknowledgedDraft = useRef<{ receipt: Receipt; draft: unknown } | null>(null);
  const valid = (value: Receipt) => value.context.identity.ownerId === ownerId && value.context.identity.scheduleId === scheduleId && value.context.isContextCurrent() && (value.state === 'success'
    ? isSelected && !isDirty && value.snapshotKey === snapshotKey(snapshot) && (acknowledgedDraft.current?.receipt !== value || acknowledgedDraft.current?.draft === draft) : value.originalDraft === draft && value.originalCurrent());
  const visible = receipt !== null && valid(receipt) ? receipt : null;
  // A committed mismatch permanently retires a displayed receipt, including A B A.
  useLayoutEffect(() => {
    if (receipt && !valid(receipt)) setReceipt(previous => previous === receipt ? null : previous);
    else if (receipt?.state === 'success' && acknowledgedDraft.current?.receipt !== receipt) acknowledgedDraft.current = { receipt, draft };
    if (receipt?.state !== 'success') acknowledgedDraft.current = null;
  });
  function begin(operation: ScheduleWriteOperation, context: ScheduleContextAuthority | null, label: ScheduleSaveLabel) {
    if (!context || !operation.isCurrent() || !context.isContextCurrent()) return null;
    const pending: Receipt = { originalDraft: draft, label: { ...label }, context, originalCurrent: operation.isLifetimeCurrent, state: 'pending' };
    setReceipt(pending);
    return {
      finish() { setReceipt(previous => previous === pending ? null : previous); },
      fail(currentReceipt: () => boolean) {
        setReceipt(previous => previous === pending && currentReceipt() && context.isContextCurrent()
          ? { ...pending, state: 'error' } : previous);
      },
      succeed(saved: SavedScheduleSnapshot, savedAt: Date, currentReceipt: () => boolean) {
        // The caller batches this receipt with its exact selected record/view/clean publication.
        const key = snapshotKey(saved), time = savedAt.toISOString();
        setReceipt(previous => previous === pending && currentReceipt() && context.isContextCurrent()
          ? { ...pending, state: 'success', snapshotKey: key, savedAt: time } : previous);
      },
    };
  }
  const outcome: ScheduleSaveOutcome | null = visible ? { ...visible.label, state: visible.state,
    ...(visible.savedAt ? { savedAt: visible.savedAt } : {}) } : null;
  return { begin, outcome };
}
